import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FiUser, FiPhone, FiMail, FiMapPin, FiClock, FiCalendar, FiCheckCircle, FiUsers, FiArrowRight } from 'react-icons/fi'
import { bookCleaningSlot, fetchPublicHeritagePlaces } from '../utils/api'

const InputField = ({ icon: Icon, label, ...props }) => (
  <div className="relative group">
    <label className="block text-[8px] sm:text-[10px] font-black uppercase tracking-[0.2em] text-gray-500 mb-1.5 sm:mb-2 group-focus-within:text-accent-maroon transition-colors">
      {label}
    </label>
    <div className="relative">
      <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-accent-maroon transition-colors">
        <Icon size={16} />
      </div>
      <input {...props} className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 md:py-4 bg-white/5 border border-gray-200 rounded-lg sm:rounded-2xl text-sm sm:text-base text-gray-800 placeholder-gray-500 focus:border-accent-maroon focus:bg-white/10 focus:outline-none transition-all duration-300" />
    </div>
  </div>
)

const CountdownTimer = ({ timingDay, timingStart }) => {
  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    if (!timingDay || !timingStart) {
      setTimeLeft('')
      return
    }

    const calculateTimeLeft = () => {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      const targetDay = days.indexOf(timingDay)
      const today = new Date().getDay()
      
      let daysUntil = targetDay - today
      if (daysUntil < 0) daysUntil += 7
      
      const [hours, minutes] = timingStart.split(':').map(Number)
      const now = new Date()
      const target = new Date(now)
      target.setDate(now.getDate() + daysUntil)
      target.setHours(hours, minutes, 0, 0)
      
      if (target <= now && daysUntil === 0) {
        target.setDate(target.getDate() + 7)
      }
      
      const diff = target - now
      const d = Math.floor(diff / (1000 * 60 * 60 * 24))
      const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const s = Math.floor((diff % (1000 * 60)) / 1000)
      
      setTimeLeft(`${d}d ${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`)
    }

    calculateTimeLeft()
    const timer = setInterval(calculateTimeLeft, 1000)
    return () => clearInterval(timer)
  }, [timingDay, timingStart])

  if (!timeLeft) return null
  return (
    <div className="flex items-center space-x-2 text-accent-maroon">
      <FiClock size={14} />
      <span className="text-sm font-mono font-bold">{timeLeft}</span>
    </div>
  )
}

const BookingForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    mobileNumber: '',
    email: '',
    location: '',
    slot: '',
  })
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState({ type: '', message: '' })
  const [places, setPlaces] = useState([])
  const [hoveredIndex, setHoveredIndex] = useState(null)

  useEffect(() => {
    const loadPlaces = async () => {
      try {
        const res = await fetchPublicHeritagePlaces()
        setPlaces(res.data?.data || [])
      } catch (err) {
        console.error('Failed to load places:', err)
      }
    }
    loadPlaces()
  }, [])

  const locations = [
    'Fateh Sagar Lake',
    'Pichola Lake',
    'Gangaur Ghat',
    'City Palace Area',
    'Local Communities',
  ]

  const slots = [
    '6 AM – 8 AM',
    '8 AM – 10 AM',
    '4 PM – 6 PM',
    '6 PM – 8 PM',
  ]

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setStatus({ type: '', message: '' })

    try {
      await bookCleaningSlot(formData)
      setStatus({ type: 'success', message: 'SLOT BOOKED! SEE YOU AT THE DRIVE.' })
      setFormData({ fullName: '', mobileNumber: '', email: '', location: '', slot: '' })
      setTimeout(() => setStatus({ type: '', message: '' }), 5000)
    } catch (error) {
      const errorData = error.response?.data
      setStatus({ type: 'error', message: errorData?.error || 'BOOKING FAILED. PLEASE TRY AGAIN.' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <section id="drives" className="relative py-16 sm:py-24 md:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden bg-white">
      <div className="absolute top-0 right-0 w-[300px] h-[300px] sm:w-[500px] sm:h-[500px] bg-accent-maroon/5 blur-[120px] rounded-full translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] sm:w-[500px] sm:h-[500px] bg-accent-maroon/3 blur-[120px] rounded-full -translate-x-1/2 translate-y-1/2" />
      
      <div className="max-w-7xl mx-auto relative">
        <div className="mb-24 flex flex-col lg:flex-row lg:items-end justify-between gap-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl">
            <h4 className="text-accent-maroon font-black tracking-[0.3em] uppercase text-xs mb-6">Discovery Mode</h4>
            <h2 className="text-6xl md:text-8xl font-black font-grotesk tracking-tighter leading-none text-gray-800">
              BOOK YOUR <br />
              <span className="bg-gradient-to-r from-accent-maroon to-accent-maroonDark bg-clip-text text-transparent">DRIVE.</span>
            </h2>
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-gray-600 text-xl font-medium max-w-sm">
            Pick your spot, choose your time, and join the frontline of Udaipur's environmental revolution. Every hour counts.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2 className="text-5xl md:text-7xl font-black font-grotesk tracking-tighter leading-tight mb-8 text-gray-800">
              BOOK YOUR <br />
              <span className="bg-gradient-to-r from-accent-maroon to-accent-maroonDark bg-clip-text text-transparent">DRIVE.</span>
            </h2>
            <p className="text-gray-600 text-xl font-medium mb-12 max-w-lg leading-relaxed">
              Pick your spot, choose your time, and join the frontline of Udaipur's environmental revolution. Every hour counts.
            </p>

            <div className="space-y-8">
              {[
                { title: 'GEAR PROVIDED', desc: 'We supply gloves, bags, and safety equipment.' },
                { title: 'FLEXIBLE TIMES', desc: 'Early morning or evening slots to fit your life.' },
                { title: 'TEAM SPIRIT', desc: 'Work alongside fellow citizens in a high-energy environment.' }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-6 group">
                  <div className="w-12 h-12 rounded-2xl bg-white/5 border border-gray-200 flex items-center justify-center text-accent-maroon group-hover:bg-accent-maroon group-hover:text-white transition-all duration-500">
                    <FiCalendar size={24} />
                  </div>
                  <div>
                    <h4 className="text-gray-800 font-black text-sm tracking-widest">{item.title}</h4>
                    <p className="text-gray-600 text-sm font-medium">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-accent-maroon to-accent-maroonDark rounded-xl sm:rounded-2xl lg:rounded-[2.5rem] blur opacity-20"></div>
            <div className="relative bg-white/10 backdrop-blur-2xl border border-gray-200 p-4 sm:p-6 md:p-8 lg:p-12 rounded-xl sm:rounded-2xl lg:rounded-[2.5rem] shadow-2xl">
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 md:space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                  <InputField icon={FiUser} label="Full Name" name="fullName" value={formData.fullName} onChange={handleChange} placeholder="Enter name" required />
                  <InputField icon={FiPhone} label="Mobile Number" name="mobileNumber" value={formData.mobileNumber} onChange={handleChange} placeholder="Enter number" required />
                </div>

                <InputField icon={FiMail} label="Email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="name@example.com" required />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                  <div className="relative group">
                    <label className="block text-[8px] sm:text-[10px] font-black uppercase tracking-[0.2em] text-gray-500 mb-1.5 sm:mb-2 group-focus-within:text-accent-maroon transition-colors">Location</label>
                    <div className="relative">
                      <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-accent-maroon transition-colors pointer-events-none">
                        <FiMapPin size={16} />
                      </div>
                      <select name="location" value={formData.location} onChange={handleChange} className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 md:py-4 bg-white/5 border border-gray-200 rounded-lg sm:rounded-2xl text-sm sm:text-base text-gray-800 focus:border-accent-maroon focus:bg-white/10 focus:outline-none transition-all appearance-none cursor-pointer" required>
                        <option value="" className="bg-white text-gray-500">Choose...</option>
                        {locations.map(loc => <option key={loc} value={loc} className="bg-white">{loc}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="relative group">
                    <label className="block text-[8px] sm:text-[10px] font-black uppercase tracking-[0.2em] text-gray-500 mb-1.5 sm:mb-2 group-focus-within:text-accent-maroon transition-colors">Time Slot</label>
                    <div className="relative">
                      <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-accent-maroon transition-colors pointer-events-none">
                        <FiClock size={16} />
                      </div>
                      <select name="slot" value={formData.slot} onChange={handleChange} className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 md:py-4 bg-white/5 border border-gray-200 rounded-lg sm:rounded-2xl text-sm sm:text-base text-gray-800 focus:border-accent-maroon focus:bg-white/10 focus:outline-none transition-all appearance-none cursor-pointer" required>
                        <option value="" className="bg-white text-gray-500">Choose...</option>
                        {slots.map(slot => <option key={slot} value={slot} className="bg-white">{slot}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <AnimatePresence>
                  {status.message && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className={`p-3 sm:p-4 rounded-lg sm:rounded-2xl text-center text-xs font-black tracking-widest uppercase ${status.type === 'success' ? 'bg-green-500/10 text-green-600 border border-green-500/20' : 'bg-red-500/10 text-red-600 border border-red-500/20'}`}>
                      {status.message}
                    </motion.div>
                  )}
                </AnimatePresence>

                <motion.button whileHover={{ scale: 1.02, boxShadow: '0 0 30px rgba(128,0,0,0.3)' }} whileTap={{ scale: 0.98 }} type="submit" disabled={loading} className="w-full py-3 sm:py-4 md:py-5 bg-gradient-to-r from-accent-maroon to-accent-maroonDark text-white font-black uppercase tracking-[0.3em] text-xs sm:text-sm rounded-lg sm:rounded-2xl transition-all shadow-xl disabled:opacity-50">
                  {loading ? 'CONFIRMING...' : 'RESERVE MY SLOT'}
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default BookingForm